import { c as _c } from "react/compiler-runtime";
import React from "react";
import { ChatBubbleLeftIcon, Squares2X2Icon } from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import { createBreakpoint } from "react-use";
import { useContentTab } from "@/components/brain/ContentTabContext";
import PrimaryButton from "@/components/utils/button/PrimaryButton";
import type { ApiWave } from "@/generated/models/ApiWave";
import type { WaveViewMode } from "@/hooks/useWaveViewMode";
import { MyStreamWaveTab } from "@/types/waves.types";
import MyStreamWaveDesktopTabs from "../MyStreamWaveDesktopTabs";
import type { ChatSubmitDropAction } from "../chatSubmitDrop.types";
import MyStreamWaveTabsHeader, { type MyStreamWaveTabsHeaderActionContext } from "./MyStreamWaveTabsHeader";
const useBreakpoint = createBreakpoint({
  LG: 1024,
  MD: 768,
  S: 0
});
interface MyStreamWaveTabsDefaultProps {
  readonly wave: ApiWave;
  readonly viewMode: WaveViewMode;
  readonly onToggleViewMode: () => void;
  readonly showGalleryToggle: boolean;
  readonly activeCurationId?: string | null | undefined;
  readonly onSelectCuration?: ((curationId: string | null) => void) | undefined;
  readonly chatSubmitDropAction?: ChatSubmitDropAction | null | undefined;
}
const MyStreamWaveTabsDefault: React.FC<MyStreamWaveTabsDefaultProps> = t0 => {
  const $ = _c(25);
  const {
    wave,
    viewMode,
    onToggleViewMode,
    showGalleryToggle,
    activeCurationId: t1,
    onSelectCuration: t2,
    chatSubmitDropAction: t3
  } = t0;
  const activeCurationId = t1 === undefined ? null : t1;
  const onSelectCuration = t2 === undefined ? _temp : t2;
  const chatSubmitDropAction = t3 === undefined ? null : t3;
  const {
    activeContentTab,
    setActiveContentTab
  } = useContentTab();
  const breakpoint = useBreakpoint();
  const showBackButton = breakpoint !== "LG";
  const isCompact = breakpoint === "S";
  const headerActionsTooltipId = `my-stream-wave-header-actions-${wave.id}`;
  const galleryToggleLabel = viewMode === "chat" ? "Switch to gallery view" : "Switch to chat view";
  let t4;
  if ($[0] !== activeCurationId || $[1] !== chatSubmitDropAction || $[2] !== galleryToggleLabel || $[3] !== onToggleViewMode || $[4] !== showGalleryToggle || $[5] !== viewMode) {
    t4 = t5 => {
      const {
        activeContentTab: headerActiveContentTab,
        tooltipId
      } = t5;
      const action = chatSubmitDropAction;
      const showChatSubmitDropAction = headerActiveContentTab === MyStreamWaveTab.CHAT && !activeCurationId && action?.isVisible === true;
      const chatSubmitDropTooltip = action ? action.restrictionMessage ?? action.label : null;
      const chatSubmitDropTitle = chatSubmitDropTooltip ?? undefined;
      return <>{showChatSubmitDropAction && <span className="tw-inline-flex" title={chatSubmitDropTitle} data-tooltip-id={tooltipId} data-tooltip-content={chatSubmitDropTooltip}><PrimaryButton loading={false} disabled={!action.canOpen} onClicked={action.onOpen} padding="tw-px-2.5 tw-py-2" title={chatSubmitDropTitle} ariaLabel={action.label}><PlusIcon className="-tw-ml-1 tw-h-4 tw-w-4 tw-flex-shrink-0" /><span>{action.compactLabel}</span></PrimaryButton></span>}{showGalleryToggle && !activeCurationId && <button type="button" onClick={onToggleViewMode} aria-label={galleryToggleLabel} data-tooltip-id={tooltipId} data-tooltip-content={galleryToggleLabel} className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900 tw-text-iron-200 tw-transition tw-duration-150 hover:tw-border-iron-500 hover:tw-bg-iron-800 hover:tw-text-white">{viewMode === "chat" ? <Squares2X2Icon className="tw-h-4 tw-w-4 tw-flex-shrink-0" /> : <ChatBubbleLeftIcon className="tw-h-4 tw-w-4 tw-flex-shrink-0" />}</button>}</>;
    };
    $[0] = activeCurationId;
    $[1] = chatSubmitDropAction;
    $[2] = galleryToggleLabel;
    $[3] = onToggleViewMode;
    $[4] = showGalleryToggle;
    $[5] = viewMode;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const renderHeaderLeadingActions = t4;
  let t5;
  if ($[7] !== activeContentTab || $[8] !== headerActionsTooltipId || $[9] !== isCompact || $[10] !== onSelectCuration || $[11] !== renderHeaderLeadingActions || $[12] !== setActiveContentTab || $[13] !== showBackButton || $[14] !== wave) {
    t5 = <MyStreamWaveTabsHeader wave={wave} activeContentTab={activeContentTab} setActiveContentTab={setActiveContentTab} onSelectCuration={onSelectCuration} isCompact={isCompact} showBackButton={showBackButton} headerActionsTooltipId={headerActionsTooltipId} headerClassName="tw-flex tw-items-center tw-justify-between tw-gap-x-4 tw-border-x-0 tw-border-b tw-border-t-0 tw-border-solid tw-border-iron-800 tw-px-2 tw-py-3 sm:tw-px-4" actionsClassName="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 tw-self-stretch" renderLeadingActions={renderHeaderLeadingActions} />;
    $[7] = activeContentTab;
    $[8] = headerActionsTooltipId;
    $[9] = isCompact;
    $[10] = onSelectCuration;
    $[11] = renderHeaderLeadingActions;
    $[12] = setActiveContentTab;
    $[13] = showBackButton;
    $[14] = wave;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== activeContentTab || $[17] !== activeCurationId || $[18] !== onSelectCuration || $[19] !== setActiveContentTab || $[20] !== wave) {
    t6 = <div className="tw-border-b tw-border-l-0 tw-border-t-0 tw-border-solid tw-border-iron-800"><MyStreamWaveDesktopTabs activeTab={activeContentTab} wave={wave} setActiveTab={setActiveContentTab} activeCurationId={activeCurationId} onSelectCuration={onSelectCuration} /></div>;
    $[16] = activeContentTab;
    $[17] = activeCurationId;
    $[18] = onSelectCuration;
    $[19] = setActiveContentTab;
    $[20] = wave;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <div className="tw-flex tw-w-full tw-flex-col tw-bg-iron-950">{t5}{t6}</div>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
};
export default MyStreamWaveTabsDefault;
function _temp() {}